import { DisplayAccountStatus, isDisplayAccountStatus, validateDisplayAccountStatus } from "./account_status";
import { ClientPortal, isClientPortal } from "./client_portal";
import { DisplayContact, isDisplayContact } from "./contact";
import { Currency, isCurrency, validateCurrency } from "./currency";
import { DisplayIndustry, isDisplayIndustry } from "./industry";
import { DisplayPhone, isDisplayPhone, validateDisplayPhone } from "./phone";
import { DisplayTag, isDisplayTag, validateDisplayTag } from "./tag";
import { DisplayTax, isDisplayTax, validateDisplayTax } from "./tax";
import { DisplayTerms, isDisplayTerms, validateDisplayTerms } from "./terms";
import { isBool, isNumber, isString } from "./type_guards";
import { DisplayUser, isDisplayUser, validateDisplayUser } from "./user";
import { validateNonEmptyString } from "./validators";

export interface DisplayClient {
    id: string
    name: string
    email: string | null
    industry: DisplayIndustry
    tenantAccountNumber: string | null
    clientRep: DisplayUser | null
    primaryContact: DisplayContact | null
    status: DisplayAccountStatus | null
    defaultTerms: DisplayTerms
    defaultTax: DisplayTax
    defaultCurrency: Currency
    tags: readonly DisplayTag[]
    phones: readonly DisplayPhone[]
    minMargin: number | null
    portal: ClientPortal | null
    salesToDate: number
    salesTarget: number
    avalaraEntityUseCode: string | null
    creditHold: boolean
    parentClientId: string | null
    parentClientName: string | null
    qboCustomerRef: string | null
    xeroContactId: string | null
    profile: string | null
    website: string | null
    facebook: string | null
    twitter: string | null
    commissionClientRateId: string | null
}

// Type guards
export const isDisplayClient = (client: unknown): client is DisplayClient => {
    if (
        typeof client !== 'object' ||
        !('id' in client) ||
        !('name' in client) ||
        !('email' in client) ||
        !('industry' in client) ||
        !('tenantAccountNumber' in client) ||
        !('status' in client) ||
        !('clientRep' in client) ||
        !('primaryContact' in client) ||
        !('defaultTerms' in client) ||
        !('defaultTax' in client) ||
        !('defaultCurrency' in client) ||
        !('tags' in client) ||
        !('phones' in client) ||
        !('minMargin' in client) ||
        !('portal' in client) ||
        !('salesToDate' in client) ||
        !('salesTarget' in client) ||
        !('avalaraEntityUseCode' in client) ||
        !('creditHold' in client) ||
        !('parentClientId' in client) ||
        !('parentClientName' in client) ||
        !('qboCustomerRef' in client) ||
        !('xeroContactId' in client) ||
        !('profile' in client) || 
        !('website' in client) ||
        !('commissionClientRateId' in client) ||
        !('facebook' in client) ||
        !('twitter' in client)
    ) {
        return false;
    }

    return (
        isString(client.id) &&
        isString(client.name) &&
        (client.email === null || isString(client.email)) &&
        isDisplayIndustry(client.industry) &&
        (client.tenantAccountNumber === null || isString(client.tenantAccountNumber)) &&
        (client.clientRep === null || isDisplayUser(client.clientRep)) &&
        (client.primaryContact === null || isDisplayContact(client.primaryContact)) &&
        (client.status === null || isDisplayAccountStatus(client.status)) &&
        isDisplayTerms(client.defaultTerms) &&
        isDisplayTax(client.defaultTax) &&
        isCurrency(client.defaultCurrency) &&
        (Array.isArray(client.tags) && client.tags.every(isDisplayTag)) &&
        (Array.isArray(client.phones) && client.phones.every(isDisplayPhone)) &&
        (client.minMargin === null || isNumber(client.minMargin)) &&
        (client.portal === null || isClientPortal(client.portal)) &&
        isNumber(client.salesToDate) &&
        isNumber(client.salesTarget) &&
        (client.avalaraEntityUseCode === null || isString(client.avalaraEntityUseCode)) &&
        isBool(client.creditHold) &&
        (client.parentClientId === null || isString(client.parentClientId)) &&
        (client.parentClientName === null || isString(client.parentClientName)) &&
        (client.qboCustomerRef === null || isString(client.qboCustomerRef)) &&
        (client.xeroContactId === null || isString(client.xeroContactId)) &&
        (client.profile === null || isString(client.profile)) &&
        (client.website === null || isString(client.website)) &&
        (client.facebook === null || isString(client.facebook)) &&
        (client.twitter === null || isString(client.twitter)) &&
        (client.commissionClientRateId === null || isString(client.commissionClientRateId))
    );
}

// Validators
export const validateDisplayClient = (client: DisplayClient) => {
    if (client.salesTarget < 0) {
        throw new Error("Sales target must be a non-negative number");
    }

    validateNonEmptyString(client.id);
    validateNonEmptyString(client.name);
    if (client.clientRep) {
        validateDisplayUser(client.clientRep);
    }
    if (client.status) {
        validateDisplayAccountStatus(client.status);
    }
    validateDisplayTerms(client.defaultTerms);
    validateDisplayTax(client.defaultTax);
    validateCurrency(client.defaultCurrency);
    client.tags.forEach(validateDisplayTag);
    client.phones.forEach(validateDisplayPhone);
}
